import React from "react";
import PropTypes from "prop-types";
import {
  CardContainer,
  ImageSection,
  ContentSection,
  Title,
  TagRow,
  ValueBadge,
  InvestorsBadge,
  Description,
  StatsRow,
  StatItem,
  StatLabel,
  StatValue,
  RiskIndicator,
  HoldingsStack,
  HoldingLogo,
  ShareButton,
} from "./HeroCard.styles";
import AssetClassTag from "shared/AssetClassTag";
import { ExternalLink } from "lucide-react";

import {
  ArrowUpProfitIcon,
  ArrowDownLossIcon,
  UsersIcon,
  EmbedLink,
  KitIcon,
  CoinsIcon,
} from "assets/icons";
import { useMemo } from "react";
import { useNavigation } from "utils/navigate";
import { getRiskLevelInfo } from "utils/riskLevel";

const HeroCard = ({
  data: {
    id,
    title,
    authorName,
    authorId,
    description,
    coverPhoto,
    investment,
    investors,
    industry,
    topHoldings,
    annualReturn,
    assetType,
    risk,
  },
  onShareClick = () => { },
}) => {
  const hasImage = coverPhoto && coverPhoto.trim() !== "";
  const { navigateToV2HostDetails } = useNavigation();

  const isCrypto = useMemo(() => {
    return assetType?.toLowerCase().includes("crypto");
  }, [assetType]);

  // Get comprehensive risk level information using new utility
  const riskInfo = useMemo(() => getRiskLevelInfo(risk), [risk]);

  // Helper function to parse and validate numeric values
  const parseNumericValue = (value) => {
    if (!value || value === "--" || value === "N/A") return null;
    const numericValue = parseFloat(value.toString().replace(/[%,$]/g, ""));
    return isNaN(numericValue) ? null : numericValue;
  };

  // Handle annual return display
  const renderAnnualReturn = () => {
    const numericReturn = parseNumericValue(annualReturn);

    if (numericReturn === null) {
      return <StatValue>--</StatValue>;
    }

    const isPositive = numericReturn > 0;
    const isNegative = numericReturn < 0;

    return (
      <StatValue positive={isPositive} negative={isNegative}>
        {isPositive ? (
          <ArrowUpProfitIcon />
        ) : isNegative ? (
          <ArrowDownLossIcon />
        ) : null}
        {annualReturn}
      </StatValue>
    );
  };

  const handleAuthorClick = () => {
    if (authorId) {
      navigateToV2HostDetails(authorId);
    }
  };

  return (
    <CardContainer title={title} id={id}>
      <ImageSection hasImage={hasImage}>
        {hasImage && <img src={coverPhoto} alt={title} />}
      </ImageSection>
      <ContentSection>
        <Title>{title}</Title>

        <TagRow>
          {assetType && <AssetClassTag assetClass={assetType} />}
          {investment && (
            <ValueBadge>
              <img src={CoinsIcon} alt="value" width="12" height="12" />
              {investment}
            </ValueBadge>
          )}
          {investors && (
            <InvestorsBadge>
              <UsersIcon width="12" height="12" stroke="#535862" />
              {`${investors} investors`}
            </InvestorsBadge>
          )}
        </TagRow>

        <Description>{description}</Description>

        <StatsRow>
          {!isCrypto && (
            <StatItem>
              <StatLabel>Top Industry</StatLabel>
              <StatValue>
                <KitIcon width="16" height="12" stroke="#535862" />
                {industry}
              </StatValue>
            </StatItem>
          )}

          <StatItem>
            <StatLabel>Top Holdings</StatLabel>
            <StatValue>
              {Array.isArray(topHoldings) && topHoldings.length > 0 ? (
                <HoldingsStack>
                  {topHoldings.slice(0, 4).map((imageUrl, index) => (
                    <HoldingLogo
                      $index={index}
                      key={index}
                      src={imageUrl}
                      alt={`Holding ${index + 1}`}
                    />
                  ))}
                </HoldingsStack>
              ) : (
                "--"
              )}
            </StatValue>
          </StatItem>

          <StatItem>
            <StatLabel>1-Year Return</StatLabel>
            {renderAnnualReturn()}
          </StatItem>

          <StatItem
            title={`${riskInfo.level} Risk: ${risk || "not available"}`}
          >
            <StatLabel>Risk</StatLabel>
            <StatValue>
              <RiskIndicator level={riskInfo.key} />
              {riskInfo.level}
            </StatValue>
          </StatItem>

          <StatItem onClick={handleAuthorClick} $clickable={!!authorId}>
            <StatLabel>By</StatLabel>
            <StatValue>
              <EmbedLink width="12" height="12" />
              {authorName}
            </StatValue>
          </StatItem>

          <ShareButton onClick={onShareClick} aria-label="Share strategy">
            <ExternalLink size={16} stroke="#414651" />
            Share
          </ShareButton>
        </StatsRow >
      </ContentSection >
    </CardContainer >
  );
};

HeroCard.propTypes = {
  data: PropTypes.shape({
    id: PropTypes.string,
    title: PropTypes.string,
    authorName: PropTypes.string,
    description: PropTypes.string,
    investment: PropTypes.string,
    investors: PropTypes.string,
    industry: PropTypes.string,
    topHoldings: PropTypes.arrayOf(PropTypes.string),
    annualReturn: PropTypes.string,
    assetType: PropTypes.string,
    risk: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    coverPhoto: PropTypes.string,
    authorId: PropTypes.string,
  }),
  onShareClick: PropTypes.func,
};

export default HeroCard;
